import { clearConsentGateState } from '@/lib/consentGate';
import { clearAppQueryCache } from '@/lib/queryClient';
import { resetAuthenticatedUser } from '@/lib/analytics/trackers/auth';

import { USER_PROFILE_UPDATED_EVENT, USER_STORAGE_KEY } from '@/lib/authStorage';
export { USER_STORAGE_KEY, USER_PROFILE_UPDATED_EVENT } from '@/lib/authStorage';
export const IDEMPOTENCY_STORAGE_PREFIX = 'cs-idempotency:';
export const LAST_ACTIVITY_STORAGE_KEY = 'caloriescience:last-activity';

/** Why a session ended; carried to `/login?reason=` so the login page can explain it. */
export type SessionEndReason =
  | 'idle'
  | 'expired'
  | 'session_idle'
  | 'session_superseded'
  | 'session_revoked'
  | 'account_disabled'
  | 'token_expired'
  | 'password_changed';

export const SESSION_END_REASON_MESSAGES: Record<SessionEndReason, string> = {
  idle: 'You were signed out after 30 minutes of inactivity',
  session_idle: 'Signed out after inactivity',
  expired: 'Your session has expired. Please sign in again',
  token_expired: 'Your session has expired. Please sign in again',
  session_superseded: 'You signed in on another device',
  session_revoked: 'Your session was signed out',
  account_disabled: 'This account has been disabled',
  password_changed: 'Password changed. Please sign in again',
};

export const isSessionEndReason = (value: unknown): value is SessionEndReason =>
  typeof value === 'string' && Object.prototype.hasOwnProperty.call(SESSION_END_REASON_MESSAGES, value);

export const getSessionEndReasonMessage = (reason: unknown, idleTimeoutMinutes?: number): string | null => {
  if (!isSessionEndReason(reason)) return null;
  if (reason === 'idle' && typeof idleTimeoutMinutes === 'number' && idleTimeoutMinutes > 0) {
    return `You were signed out after ${idleTimeoutMinutes} minutes of inactivity`;
  }
  return SESSION_END_REASON_MESSAGES[reason];
};

// Reads `exp` without verifying the signature: this only decides whether the
// stored session can still be used before the API rejects it.
export const isJwtExpired = (token: string, now: number = Date.now()): boolean => {
  const payloadSegment = token.split('.')[1];
  if (!payloadSegment) {
    return false;
  }

  try {
    const base64 = payloadSegment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const payload = JSON.parse(atob(padded)) as { exp?: unknown };
    return typeof payload.exp === 'number' && payload.exp * 1000 <= now;
  } catch {
    return false;
  }
};

export const readStoredAuthToken = (): string | null => {
  try {
    const user = JSON.parse(localStorage.getItem(USER_STORAGE_KEY) || 'null') as { token?: unknown } | null;
    return typeof user?.token === 'string' && user.token ? user.token : null;
  } catch {
    return null;
  }
};

/** True when a session is stored but its JWT `exp` has already passed. */
export const hasExpiredStoredAuthSession = (): boolean => {
  const token = readStoredAuthToken();
  return Boolean(token) && isJwtExpired(token as string);
};

// Retry-safe request keys belong to the signed-in user and must not outlive the session.
const clearRetrySafeIdempotencyKeys = () => {
  try {
    Object.keys(sessionStorage)
      .filter((key) => key.startsWith(IDEMPOTENCY_STORAGE_PREFIX))
      .forEach((key) => sessionStorage.removeItem(key));
  } catch { /* no-op */ }
};

export const clearStoredAuthSession = () => {
  if (typeof window === 'undefined') {
    return;
  }

  clearAppQueryCache();
  // Cookie consent lives in a cookie, so clearing localStorage keeps the visitor's choice.
  localStorage.clear();
  // Session-scoped state (MFA email, OAuth state, drafts, idempotency keys)
  // must not survive a sign-out on a shared machine.
  try { sessionStorage.clear(); } catch { /* no-op */ }
  clearRetrySafeIdempotencyKeys();
  clearConsentGateState();
  resetAuthenticatedUser();
  window.dispatchEvent(new CustomEvent(USER_PROFILE_UPDATED_EVENT));
};
